// One board column: tinted header (lanes neutral, people's steps signal,
// decisions info, Done with outcome chips) and its cards. The column is a
// native drop target; the board decides what a drop means.

import type { DragEvent, ReactNode } from 'react'
import type { BoardCard as BoardCardData, BoardColumn as BoardColumnData, StreamDefinition } from '@goblic/shared'
import { BoardCard } from './board-card'
import { columnTint, isBugStream, isLaneColumn, slotIndexFor, type DropSlot } from './board-utils'
import { MomentArt } from '../goblin-art'

const OUTCOME_CHIP: Record<'success' | 'failure' | 'cancelled', string> = {
  success: 'bg-ok-soft text-ok',
  failure: 'bg-danger-soft text-danger',
  cancelled: 'bg-sunken text-ink-2',
}

export interface BoardColumnProps {
  column: BoardColumnData
  cards: BoardCardData[]
  stream: StreamDefinition
  otherStreams: StreamDefinition[]
  /** The card being dragged, if any. */
  draggedId: string | null
  /** Whether the dragged card may be dropped here. */
  canDrop: boolean
  slot: DropSlot | null
  busy: boolean
  onSlot: (slot: DropSlot | null) => void
  onDrop: (columnId: string) => void
  onDragStart: (card: BoardCardData) => void
  onDragEnd: () => void
  onOpen: (card: BoardCardData) => void
  onStartNow: (card: BoardCardData) => void
  onMoveStream: (card: BoardCardData, streamKey: string) => void
  onUnpin: (card: BoardCardData) => void
  onArchive: (card: BoardCardData) => void
}

export function BoardColumn({
  column,
  cards,
  stream,
  otherStreams,
  draggedId,
  canDrop,
  slot,
  busy,
  onSlot,
  onDrop,
  onDragStart,
  onDragEnd,
  onOpen,
  onStartNow,
  onMoveStream,
  onUnpin,
  onArchive,
}: BoardColumnProps) {
  const lane = isLaneColumn(column)
  const tint = columnTint(column)
  const siblings = cards.filter((c) => c.id !== draggedId)
  const slotHere = slot?.columnId === column.id ? slot : null
  const hidden = Math.max(0, column.count - cards.length)

  function handleDragOver(event: DragEvent<HTMLElement>): void {
    if (!draggedId || !canDrop) return
    event.preventDefault()
    event.dataTransfer.dropEffect = 'move'
    // Over a card: the card reports its own slot. Over empty space: the end.
    if (!(event.target as HTMLElement).closest('li') && lane) {
      if (slotHere?.index !== siblings.length) onSlot({ columnId: column.id, index: siblings.length })
    } else if (!lane && !slotHere) {
      onSlot({ columnId: column.id, index: siblings.length })
    }
  }

  function handleDragLeave(event: DragEvent<HTMLElement>): void {
    if (!event.currentTarget.contains(event.relatedTarget as Node | null) && slotHere) onSlot(null)
  }

  function handleDrop(event: DragEvent<HTMLElement>): void {
    if (!draggedId || !canDrop) return
    event.preventDefault()
    onDrop(column.id)
  }

  const indicator = <li aria-hidden className="h-0.5 rounded-full bg-moss" />

  return (
    <section
      aria-label={column.label}
      className={`flex min-h-0 w-72 shrink-0 flex-col rounded-panel border ${
        draggedId && canDrop ? (slotHere ? 'border-moss' : 'border-line-strong border-dashed') : 'border-line'
      } ${tint.body}`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <header className={`flex flex-wrap items-center gap-x-2 gap-y-1 rounded-t-panel px-3 py-2 ${tint.header}`}>
        <h3 className="font-display text-base font-semibold">{column.label}</h3>
        <span className="rounded-full bg-surface/70 px-1.5 text-xs font-semibold tabular-nums">{column.count}</span>
        {column.kind === 'human' ? <span className="text-xs">people</span> : null}
        {column.kind === 'decision' ? <span className="text-xs">decision</span> : null}
        {column.kind === 'done' && column.terminalStates ? (
          <span className="flex w-full flex-wrap gap-1">
            {column.terminalStates.map((t) => (
              <span key={t.name} className={`rounded-full px-1.5 text-xs font-medium ${OUTCOME_CHIP[t.outcome]}`}>
                {t.name}
              </span>
            ))}
          </span>
        ) : null}
      </header>

      <ul className="flex min-h-[4rem] flex-1 flex-col gap-2 overflow-y-auto p-2">
        {/* Not while the column's only card is being dragged: the placeholder would push that card down
            under the pointer and the browser cancels the drag. */}
        {cards.length === 0 && !slotHere ? (
          <li className="flex flex-col items-center gap-1 px-1 py-3 text-center text-xs text-ink-3">
            {/* The stream's front door gets a goblin (bug hunting in a bug stream, sorting elsewhere);
                every other empty column a dozing one, or one with an empty tray where people review. */}
            <MomentArt
              moment={
                column.lane === 'inbox' || (lane && !stream.lanes.inbox && column.lane === 'backlog')
                  ? isBugStream(stream)
                    ? 'bug-hunt'
                    : 'sorting'
                  : column.kind === 'human'
                    ? 'column-empty-people'
                    : 'column-empty'
              }
              size={72}
            />
            {lane ? 'Empty' : 'Nothing here'}
          </li>
        ) : null}
        {cards.map((card) => {
          const siblingIndex = siblings.findIndex((c) => c.id === card.id)
          const dragged = card.id === draggedId
          return (
            <FragmentWithIndicator
              key={card.id}
              before={lane && slotHere && !dragged && slotHere.index === siblingIndex ? indicator : null}
            >
              <BoardCard
                card={card}
                stream={stream}
                otherStreams={otherStreams}
                rankNumber={lane ? cards.indexOf(card) + 1 : null}
                dragging={dragged}
                busy={busy}
                onDragStart={onDragStart}
                onDragEnd={onDragEnd}
                onDragOverCard={(over, upperHalf) => {
                  if (!draggedId || !canDrop || over.id === draggedId) return
                  const index = slotIndexFor(siblings, over.id, upperHalf)
                  if (lane && !(slotHere && slotHere.index === index)) onSlot({ columnId: column.id, index })
                }}
                onOpen={onOpen}
                onStartNow={onStartNow}
                onMoveStream={onMoveStream}
                onUnpin={onUnpin}
                onArchive={onArchive}
              />
            </FragmentWithIndicator>
          )
        })}
        {lane && slotHere && slotHere.index >= siblings.length ? indicator : null}
        {hidden > 0 ? <li className="px-1 text-center text-xs text-ink-3">+ {hidden} more</li> : null}
      </ul>
    </section>
  )
}

function FragmentWithIndicator({ before, children }: { before: ReactNode; children: ReactNode }) {
  return (
    <>
      {before}
      {children}
    </>
  )
}
